import { useState, useEffect } from "react";
import { api, createPollingSubscription } from "@/lib/apiClient";
import logger from '@/lib/logger';

export interface MetricDataPoint {
  time: string;
  activeOrders: number;
  inProgressProcesses: number;
  activeOperators: number;
  completionRate: number;
}

export const useMetricsHistory = (areaFilter: string = "all") => {
  const [history, setHistory] = useState<MetricDataPoint[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAndStoreMetrics = async () => {
    try {
      const now = new Date();
      const timeStr = now.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

      // Build queries with area filter
      let processQuery = api
        .from("process_records")
        .select("*, profiles!inner(area)", { count: "exact", head: true })
        .in("status", ["in_progress", "paused"]);
      
      if (areaFilter !== "all") {
        processQuery = processQuery.eq("profiles.area", areaFilter as any);
      }

      const { count: inProgressProcesses } = await processQuery;

      // Active work orders with area filter
      let ordersQuery = api
        .from("work_orders")
        .select("*, process_records!inner(profiles!inner(area))", { count: "exact", head: true })
        .in("status", ["pending", "in_progress"]);
      
      if (areaFilter !== "all") {
        ordersQuery = ordersQuery.eq("process_records.profiles.area", areaFilter as any);
      }

      const { count: activeOrders } = await ordersQuery;

      // Active operators
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      let activeOpsQuery = api
        .from("process_records")
        .select("operator_id, profiles!inner(area)")
        .gte("started_at", today.toISOString())
        .in("status", ["in_progress", "paused"]);
      
      if (areaFilter !== "all") {
        activeOpsQuery = activeOpsQuery.eq("profiles.area", areaFilter as any);
      }

      const { data: activeOps } = await activeOpsQuery;
      const uniqueOperators = new Set(activeOps?.map((op) => op.operator_id) || []);

      // Completion rate
      let completedQuery = api
        .from("process_records")
        .select("*, profiles!inner(area)", { count: "exact", head: true })
        .gte("started_at", today.toISOString())
        .eq("status", "completed");
      
      if (areaFilter !== "all") {
        completedQuery = completedQuery.eq("profiles.area", areaFilter as any);
      }

      const { count: completedToday } = await completedQuery;

      let totalQuery = api
        .from("process_records")
        .select("*, profiles!inner(area)", { count: "exact", head: true })
        .gte("started_at", today.toISOString());
      
      if (areaFilter !== "all") {
        totalQuery = totalQuery.eq("profiles.area", areaFilter as any);
      }

      const { count: totalToday } = await totalQuery;

      const completionRate = totalToday && totalToday > 0 
        ? Math.round((completedToday || 0) / totalToday * 100) 
        : 0;

      const newDataPoint: MetricDataPoint = {
        time: timeStr,
        activeOrders: activeOrders || 0,
        inProgressProcesses: inProgressProcesses || 0,
        activeOperators: uniqueOperators.size,
        completionRate,
      };

      setHistory((prev) => {
        // Keep only today's data
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        
        // Add new point and keep last 60 points (1 hour of minute-by-minute data)
        const updated = [...prev, newDataPoint].slice(-60);
        return updated;
      });

      setLoading(false);
    } catch (error) {
      logger.error("Error fetching metrics history:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial fetch
    fetchAndStoreMetrics();

    // Update every minute
    const interval = setInterval(fetchAndStoreMetrics, 60000);

    // Subscribe to polling for immediate updates (cada 30 segundos)
    const pollingSubscription = createPollingSubscription(
      fetchAndStoreMetrics,
      30000 // 30 segundos
    );

    return () => {
      clearInterval(interval);
      pollingSubscription.unsubscribe();
    };
  }, [areaFilter]);

  return { history, loading };
};
